/**
 * ReviewStep component
 * Final step displaying application summary before submission
 */
import React from 'react'
import { useApplicationFormStore } from '../../stores/application-form-store'
import { useTranslation } from '../../hooks/useTranslation'

export const ReviewStep: React.FC = () => {
  const {
    candidateFullName,
    candidateEmail,
    candidatePhone,
    candidateLinkedIn,
    cvFileKey,
    cvFileName,
    cvMimeType,
    prevStep,
    currentStep
  } = useApplicationFormStore()
  const { t } = useTranslation()

  const hasRequiredInfo = candidateFullName && candidateEmail && candidatePhone && cvFileKey

  if (!hasRequiredInfo) {
    return (
      <div className="text-center py-8">
        <p className="text-card-text-secondary">
          {t('application.review.missingInfo')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Candidate Information Section */}
      <div className="bg-gray-50 rounded-lg p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-card-text">
            {t('application.review.contactInfo')}
          </h4>
          <button
            type="button"
            onClick={() => {
              while (currentStep > 1) {
                prevStep()
              }
            }}
            className="
              text-sm font-medium text-primary
              hover:text-primary-hover hover:underline
              focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:rounded
              transition-colors
            "
            aria-label={t('application.review.editContactAriaLabel')}
          >
            {t('common.actions.edit')}
          </button>
        </div>

        <dl className="space-y-3">
          <div>
            <dt className="text-sm font-medium text-card-text-secondary">{t('application.review.fullName')}</dt>
            <dd className="mt-1 text-base text-card-text">{candidateFullName}</dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-card-text-secondary">{t('application.review.email')}</dt>
            <dd className="mt-1 text-base text-card-text">{candidateEmail}</dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-card-text-secondary">{t('application.review.phone')}</dt>
            <dd className="mt-1 text-base text-card-text">{candidatePhone}</dd>
          </div>

          {candidateLinkedIn && (
            <div>
              <dt className="text-sm font-medium text-card-text-secondary">{t('application.review.linkedIn')}</dt>
              <dd className="mt-1 text-base">
                <a
                  href={candidateLinkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-hover hover:underline"
                >
                  {candidateLinkedIn}
                </a>
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* CV Information Section */}
      <div className="bg-gray-50 rounded-lg p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-card-text">
            {t('application.review.cv')}
          </h4>
          <button
            type="button"
            onClick={() => prevStep()}
            className="
              text-sm font-medium text-primary
              hover:text-primary-hover hover:underline
              focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:rounded
              transition-colors
            "
            aria-label={t('application.review.editCvAriaLabel')}
          >
            {t('common.actions.edit')}
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <svg
              className="w-10 h-10 text-card-text-secondary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-medium text-card-text truncate">
              {cvFileName}
            </p>
            <p className="text-sm text-card-text-secondary">
              {cvMimeType}
            </p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <p className="text-sm text-card-text">
          <strong>{t('application.review.readyToSubmit')}</strong> {t('application.review.readyToSubmitDescription')}
        </p>
      </div>
    </div>
  )
}
